import { z } from 'zod';

import { ErrorResponse } from './common.js';

export const ImportSessionStartRequestSchema = z.object({
  platform: z.string(),
  tileSettingsId: z.string(),
});

export const ExternalStatusTypeSchema = z.enum([
  'draft',
  'active',
  'paused',
  'archived',
]);

export const EntityTypeSchema = z.enum(['campaign', 'ad_group', 'ad']);

const BaseImportResponseItemSchema = z.object({
  id: z.string(),
  name: z.string(),
  executionStatus: ExternalStatusTypeSchema,
  externalExecutionStatus: z.string(),
  externalExecutionStatusLabel: z.string(),
  alias: z.string(),
  aliasLabel: z.string(),
  selected: z.boolean().optional(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
});

export const ImportCampaignSchema = z.object({
  ...BaseImportResponseItemSchema.shape,
  entityType: EntityTypeSchema,
  isDisabled: z.boolean(),
  totalAdGroupsSelected: z.number().optional(),
  totalAdsSelected: z.number().optional(),
});

const BaseImportResponseType = z.object({
  pageNumber: z.number().nullable().optional(),
  itemsPerPage: z.number().nullable().optional(),
  totalItems: z.number().nullable().optional(),
  ordering: z.null().optional(),
  additional: z
    .object({
      loadFinished: z.boolean().optional(),
      selectedCount: z.number().optional(),
      totalFiltered: z.number().optional(),
    })
    .nullable()
    .optional(),
  message: z.string().nullable().optional(),
});

export const ImportSessionStartResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        importSessionId: z.string(),
      }),
    ),
  }),
  error: z.null().optional(),
});

export const PlatformConfigurationQuerySchema = z.object({
  platform: z.string(),
});

export const PlatformConfigurationResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        importRelevantEntities: z.array(z.string()),
        aliasesMap: z.record(z.string(), z.string()),
        aliasesMapLabels: z.record(z.string(), z.string()),
        excludeDisplayFields: z.record(z.string(), z.array(z.string())),
        externalExecutionStatusAliasesMap: z.record(z.string(), z.string()),
        autoImportEnabled: z.boolean().optional(),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const TileSettingsListQuerySchema = z.object({
  tileId: z.string(),
});

const TileSettingsItemSchema = z.object({
  id: z.string(),
  tileId: z.string(),
  campaignCollectionId: z.string(),
  autoSyncEnabled: z.boolean(),
  hasChildren: z.boolean(),
  channel: z.string().optional().nullable(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
});

export const TileSettingsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(TileSettingsItemSchema),
  }),
  error: z.string().nullable().optional(),
});

export const FetchImportCampaignsRequestSchema = z.object({
  importSessionId: z.string(),
  tileSettingsId: z.string(),
  platform: z.string(),
  selection: z.string(),
  queries: z.array(z.string()),
  pageNumber: z.number(),
  itemsPerPage: z.number(),
  statuses: z.optional(z.array(z.string())),
});

export const FetchImportCampaignsOldRequestSchema = z.object({
  tileSettingsId: z.string(),
  platform: z.string(),
  query: z.string().optional(),
  pageNumber: z.number().optional(),
  itemsPerPage: z.number().optional(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
});

export const FetchImportCampaignsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(ImportCampaignSchema),
  }),
  error: z.string().nullable().optional(),
});

export const SelectAdGroupSchema = z.object({
  campaignId: z.string(),
  adGroupId: z.string(),
  selected: z.boolean(),
});

export const SelectAdSchema = z.object({
  adGroupId: z.string(),
  adId: z.string(),
  selected: z.boolean(),
});

export const SelectItemsRequestSchema = z.object({
  tileSettingsId: z.string(),
  importSessionId: z.string(),
  campaigns: z.array(
    z.object({
      campaignId: z.string(),
      selected: z.boolean().optional(),
    }),
  ),
  adGroups: z.array(SelectAdGroupSchema),
  ads: z.array(SelectAdSchema),
});

export const SelectItemResponseSchema = z.object({
  totalCampaignsSelected: z.number(),
  totalAdGroupsSelected: z.number(),
  selectedAdGroupsByCampaign: z.array(
    z.object({
      campaignId: z.string(),
      totalAdGroupsSelected: z.number(),
    }),
  ),
  selectedAdsByAdGroup: z.array(
    z.object({
      adGroupId: z.string(),
      totalAdsSelected: z.number(),
    }),
  ),
  selectedAdsByCampaign: z
    .array(
      z.object({
        campaignId: z.string(),
        totalAdsSelected: z.number(),
      }),
    )
    .optional(),
});

export const SelectItemsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(SelectItemResponseSchema),
  }),
  error: z.string().nullable().optional(),
});

export const FetchLoadMoreRequestSchema = z.object({
  tileSettingsId: z.string(),
  platform: z.string(),
  importSessionId: z.string(),
  campaign: z.string().nullable().optional(),
  adGroup: z.string().nullable().optional(),
});

export const FetchLoadMoreResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        fullyCached: z.boolean(),
        inCacheCount: z.number(),
        entityType: z.string(),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const BackToDefaultSelectionRequestSchema = z.object({
  tileSettingsId: z.string(),
  importSessionId: z.string(),
  entityType: z.string(),
  parentId: z.string().nullable().optional(),
});

export const BackToDefaultSelectionResponseSchema = z.string().nullable();

export const FetchOmniStatusesRequestSchema = z.object({
  level: z.string(),
  platform: z.string(),
});

export const FetchOmniStatusesResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        level: z.string(),
        platform: z.string(),
        supportedStatuses: z.array(z.string()),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const FetchAdGroupsRequestSchema = z.object({
  tileSettingsId: z.string(),
  platform: z.string(),
  queries: z.array(z.string()).nullable(),
  pageNumber: z.number(),
  itemsPerPage: z.number(),
  campaignId: z.string(),
  importSessionId: z.string(),
  selection: z.string(),
  includeDisabled: z.boolean(),
});

export const FetchAdGroupResponseItemSchema = z.object({
  ...ImportCampaignSchema.shape,
  campaignId: z.string(),
});

export const FetchAdGroupsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(FetchAdGroupResponseItemSchema),
  }),
  error: ErrorResponse.optional(),
});

export const FetchAdsRequestSchema = z.object({
  tileSettingsId: z.string(),
  importSessionId: z.string(),
  adGroupId: z.string(),
  platform: z.string(),
  pageNumber: z.number(),
  itemsPerPage: z.number(),
  selection: z.string(),
  includeDisabled: z.boolean(),
  queries: z.array(z.string()).nullable(),
});

export const FetchAdResponseItemSchema = z.object({
  ...ImportCampaignSchema.shape,
  adGroupId: z.string(),
});

export const FetchAdsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        ...ImportCampaignSchema.shape,
        adGroupId: z.string(),
      }),
    ),
  }),
  error: ErrorResponse.optional(),
});

export const LaunchImportRequestSchema = z.object({
  tileSettingsId: z.string(),
  platform: z.string(),
  importSessionId: z.string(),
});

export const LaunchImportResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(z.object({ taskId: z.string() })),
  }),
  error: z.string().nullable().optional(),
});

const ValidationItemSchema = z.object({
  id: z.string(),
  entityType: EntityTypeSchema,
  campaignId: z.string().nullable().optional(),
  adGroupId: z.string().nullable().optional(),
});

export const ValidateCampaignsRequestSchema = z.object({
  platform: z.string(),
  tileSettingsId: z.string(),
  items: z.array(ValidationItemSchema),
});

export const ValidateAdGroupsRequestSchema = z.object({
  platform: z.string(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
  items: z.array(ValidationItemSchema),
});

export const ValidateCampaignsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        ...BaseImportResponseItemSchema.shape,
        adGroupsCount: z.number(),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const ValidateAdGroupsResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        ...BaseImportResponseItemSchema.shape,
        adsCount: z.number(),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const FetchAdGroupsOldRequestSchema = z.object({
  tileSettingsId: z.string(),
  platform: z.string(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
  query: z.string().optional(),
  pageNumber: z.number().optional(),
  itemsPerPage: z.number().optional(),
  items: z.array(ValidationItemSchema),
});

const ImportAdGroupSchema = z.object({
  ...ImportCampaignSchema.shape,
  campaignId: z.string(),
});

export const FetchAdGroupsOldResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        ...ImportCampaignSchema.shape,
        subRows: z.array(ImportAdGroupSchema),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const FetchSummaryOldRequestSchema = z.object({
  platform: z.string(),
  items: z.array(ValidationItemSchema),
  pageNumber: z.number().optional(),
  itemsPerPage: z.number().optional(),
  integrationId: z.string().optional(),
  adAccountId: z.string().optional(),
});

export const FetchSummaryOldResponseSchema = z.object({
  data: z.object({
    ...BaseImportResponseType.shape,
    items: z.array(
      z.object({
        ...BaseImportResponseItemSchema.shape,
        adGroupsCount: z.number(),
        adsCount: z.number(),
        subRows: z.array(
          z.object({
            ...BaseImportResponseItemSchema.shape,
            adsCount: z.number(),
            subRows: z.array(BaseImportResponseItemSchema),
          }),
        ),
      }),
    ),
  }),
  error: z.string().nullable().optional(),
});

export const LaunchImportOldQuerySchema = z.object({
  tileId: z.string(),
  platform: z.string(),
});

export const LaunchImportOldRequestSchema = z.object({
  importEntities: z.array(ValidationItemSchema).or(
    z.array(
      z.object({
        entityType: z.string(),
        id: z.string(),
        adGroupId: z.string().nullable(),
        campaignId: z.string().nullable(),
      }),
    ),
  ),
});
